import React from 'react';
import { useAuth } from '../hooks/useApi';

const pageStyle: React.CSSProperties = {
  maxWidth: 980,
  margin: '32px auto',
  padding: '0 16px 24px',
  fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
  lineHeight: 1.65
};

const leadStyle: React.CSSProperties = {
  color: '#4b5563',
  marginBottom: 16
};

const cardStyle: React.CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  padding: '14px 16px',
  background: '#fff',
  boxShadow: '0 4px 18px rgba(0,0,0,0.05)',
  marginBottom: 14
};

const listStyle: React.CSSProperties = {
  color: '#374151',
  paddingLeft: 20,
  margin: '8px 0 0'
};

const HowToPage: React.FC = () => {
  const { user, loading } = useAuth();
  const showAuthCta = !loading && !user;

  return (
    <div style={pageStyle}>
      <h1 style={{ marginBottom: 10 }}>How to use LoConvo</h1>
      <p style={leadStyle}>
        This guide follows the actual in-app workflow: find a place, start or join a topic, organize what you care about, and control who can see it.
      </p>

      <div style={cardStyle}>
        <h3 style={{ margin: 0 }}>Quick start (first 5 minutes)</h3>
        <ol style={listStyle}>
          <li>Sign up with Google, Microsoft, or email and set your username.</li>
          <li>Use the map to explore nearby topics, then open one to read comments.</li>
          <li>Open <strong>Search</strong> in the side panel to search people, topics, and comments.</li>
          <li>Create a topic with a title, description, visibility, and map location.</li>
          <li>Save useful posts to a list so your map becomes a reusable collection.</li>
        </ol>
      </div>

      <div style={cardStyle}>
        <h3 style={{ margin: 0 }}>Find content fast</h3>
        <ul style={listStyle}>
          <li><strong>Map search:</strong> Use the globe icon to find a place or address and jump the map there.</li>
          <li><strong>Find me:</strong> Use <strong>Find me</strong> to center on your current location.</li>
          <li><strong>Search drawer:</strong> Use the panel <strong>Search</strong> button to search users, topics, and comments.</li>
          <li><strong>Location filter:</strong> In Search, pick a map center and set a radius (km) to focus results.</li>
          <li><strong>Feed tabs:</strong> <strong>Popular</strong> ranks topics by votes, <strong>Live</strong> shows recently active discussions (new topics and fresh comments rise to the top), and <strong>Private</strong> (signed-in users) lists your non-public topics.</li>
        </ul>
      </div>

      <div style={cardStyle}>
        <h3 style={{ margin: 0 }}>Create a topic that works</h3>
        <ol style={listStyle}>
          <li><strong>Choose location:</strong> Click the map, or use an address pin and choose <strong>Create topic with this pin</strong>.</li>
          <li><strong>Add context:</strong> Write a clear title and description, then pick the default basemap.</li>
          <li><strong>Set visibility:</strong> Public, Only me, Followers, Mutuals, or Specific people.</li>
          <li><strong>For Specific people:</strong> Search usernames and select at least one person before posting.</li>
          <li><strong>Post anonymously (optional):</strong> Tick <strong>Post anonymously</strong> and other users see “Anonymous” instead of your name. The topic stays off your public profile, and a 🕶 badge confirms it worked — you still see your own name and can edit or delete as usual.</li>
          <li><strong>Add geometry:</strong> Draw a Point, Line, or Area to show exact context; use Undo/Clear/Finish tools.</li>
          <li><strong>Mark sensitive posts:</strong> Enable NSFW/sensitive when relevant.</li>
          <li><strong>Save Draft:</strong> Keep unfinished topics and publish later.</li>
        </ol>
      </div>

      <div style={cardStyle}>
        <h3 style={{ margin: 0 }}>Join discussion and organize your map</h3>
        <ul style={listStyle}>
          <li>Comment, reply, and vote on topics. Tick <strong>Comment anonymously</strong> when you’d rather not attach your name.</li>
          <li><strong>Stay in the loop:</strong> The bell shows when someone comments on your topic, replies to you, or when people you follow post — click a notification (or its toast) to jump straight to the topic.</li>
          <li>Save topics/comments to existing lists or create a new list while saving.</li>
          <li>Set list privacy: Public, Only me, Followers, Mutuals, or Specific people.</li>
          <li>Use profile settings to manage which topics/comments appear on your public profile.</li>
          <li>Follow users and use Messages to continue conversations directly.</li>
        </ul>
      </div>

      <div style={cardStyle}>
        <h3 style={{ margin: 0 }}>Safety checklist</h3>
        <ul style={listStyle}>
          <li>Do not publish precise private addresses without consent.</li>
          <li>Use report actions on topics/comments for abuse or unsafe content.</li>
          <li>Use private/custom visibility for sensitive plans and personal details, or post anonymously when the place matters but your name doesn’t.</li>
          <li><strong>Hide from search engines:</strong> In your profile settings, turn off <strong>Search engine visibility</strong> and your topic pages are excluded from Google’s index and your comments are left out of public topic pages. Already-indexed pages can take a while to disappear.</li>
          <li>LoConvo is not an emergency service; contact local authorities for urgent help.</li>
          <li>Review <a href="/terms">Terms</a> and <a href="/privacy">Privacy Notice</a> for full policy details.</li>
        </ul>
      </div>

      {showAuthCta && (
        <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
          <a href="/register" style={{ padding: '10px 14px', borderRadius: 8, background: '#2563eb', color: 'white', fontWeight: 700, textDecoration: 'none' }}>Create account</a>
          <a href="/login" style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid #2563eb', color: '#2563eb', fontWeight: 700, textDecoration: 'none' }}>Log in</a>
        </div>
      )}

      {!showAuthCta && (
        <p style={{ color: '#4b5563', marginTop: 16 }}>
          Need help with a specific workflow? Contact <a href="mailto:support@loconvo.com" style={{ color: '#2563eb' }}>support@loconvo.com</a>.
        </p>
      )}
    </div>
  );
};

export default HowToPage;
